import { useCallback, useRef, useState } from 'react';

const DEFAULT_SCROLL_TOP_THRESHOLD_PX = 5;

/**
 * Tracks scroll position of a container and provides scroll-to-top.
 * Use with a scrollable element by attaching scrollContainerRef and onScroll.
 * getIsAtTop() returns the latest value without causing re-renders (for imperative use).
 */
export function useScrollToTop(scrollTopThresholdPx = DEFAULT_SCROLL_TOP_THRESHOLD_PX) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const isAtTopRef = useRef(true);

  const onScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const atTop = el.scrollTop <= scrollTopThresholdPx;
    isAtTopRef.current = atTop;
    setIsAtTop(atTop);
  }, [scrollTopThresholdPx]);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getIsAtTop = useCallback(() => isAtTopRef.current, []);

  return {
    scrollContainerRef,
    isAtTop,
    onScroll,
    scrollToTop,
    getIsAtTop,
  };
}
